/**
 * StatusBadge Component
 *
 * Displays a colored badge for user/company status.
 * - Active: Green
 * - Inactive: Gray
 * - Suspended: Red
 * - Pending: Yellow
 */

import React from 'react';
import { CheckCircle, XCircle, Ban, Clock } from 'lucide-react';
import type { UserStatus, CompanyStatus } from '../../services/adminApi';

type Status = UserStatus | CompanyStatus;

interface StatusBadgeProps {
  status: Status;
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
  className?: string;
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  size = 'md',
  showIcon = true,
  className = '',
}) => {
  const statusConfig: Record<Status, { bg: string; text: string; border: string; label: string; Icon: React.ElementType }> = {
    active: {
      bg: 'bg-green-100',
      text: 'text-green-700',
      border: 'border-green-300',
      label: 'Active',
      Icon: CheckCircle,
    },
    inactive: {
      bg: 'bg-gray-100',
      text: 'text-gray-600',
      border: 'border-gray-300',
      label: 'Inactive',
      Icon: XCircle,
    },
    suspended: {
      bg: 'bg-red-100',
      text: 'text-red-700',
      border: 'border-red-300',
      label: 'Suspended',
      Icon: Ban,
    },
    pending: {
      bg: 'bg-yellow-100',
      text: 'text-yellow-700',
      border: 'border-yellow-400',
      label: 'Pending',
      Icon: Clock,
    },
  };

  const sizeConfig = {
    sm: { text: 'text-xs px-2 py-0.5', icon: 'w-3 h-3' },
    md: { text: 'text-sm px-2.5 py-1', icon: 'w-3.5 h-3.5' },
    lg: { text: 'text-base px-3 py-1.5', icon: 'w-4 h-4' },
  };

  const config = statusConfig[status] || statusConfig.inactive;
  const sizes = sizeConfig[size];

  return (
    <span
      className={`
        inline-flex items-center gap-1.5 font-medium rounded-full border
        ${config.bg} ${config.text} ${config.border}
        ${sizes.text}
        ${className}
      `}
    >
      {showIcon && <config.Icon className={sizes.icon} />}
      {config.label}
    </span>
  );
};

export default StatusBadge;
